import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BoltIcon from "@mui/icons-material/Bolt";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { tokens } from "../theme";
import type { SignagePlaylist } from "@shared/types/signage";
import { channelPosterUrl } from "@shared/signage";

/**
 * Change what a screen is playing.
 *
 * ## Why the first question is HOW, not WHAT
 *
 * The two answers write to completely different places. "Now" is an OVERRIDE -
 * a manual takeover that outranks the week and holds until it is cleared or
 * expires. "Scheduled" is the weekly plan. Asking for a playlist first and the
 * timing second would let somebody pick content and then discover the mode
 * they wanted lives on another page.
 *
 * ## Why "now" is the only one this dialog finishes
 *
 * An override is one row: a playlist and a window. A schedule is a week of day
 * plans built from playlists, which is an editor, not a dialog - and it
 * already exists. Offering a cut-down version here would be a fourth place
 * that has to agree with the other three about what a screen plays, and the
 * one most likely to disagree.
 */

export interface SignageChangeChannelDialogProps {
  open: boolean;
  /**
   * What the takeover lands on - one screen, or a group of them.
   *
   * The dialog is identical either way, because the decision is the same: an
   * override outranks the schedule for a while, then stops. What differs is the
   * BLAST RADIUS, which is why the count is a prop rather than the "1 screen"
   * this used to hardcode - forcing a channel onto eight walls is a different
   * act from forcing it onto one, and the dialog should say which is happening.
   */
  targetName: string;
  targetScreens: number;
  /** What can be put on. Empty = nothing to switch to, and the dialog says so. */
  playlists: SignagePlaylist[];
  onClose: () => void;
  /**
   * Take the screen over now, until `until` - or until somebody clears it.
   *
   * An ISO INSTANT rather than a length. The dialog asks "when should this
   * stop", so it should hand over the answer to that question; converting to
   * minutes here and back to an instant in the caller left two places doing
   * arithmetic on the same clock, and neither of them owned it.
   */
  onPlayNow: (playlistId: string, until: string | null) => Promise<void>;
  /** Hand off to the scheduler. */
  onSchedule: () => void;
}

type Mode = "now" | "scheduled";

/** Whether the takeover ends at a chosen moment, or not at all. */
type EndMode = "open" | "until";

const ALL_CATEGORIES = "__all__";

/**
 * The channel strip's geometry, stated rather than emerged.
 *
 * The rail used to size itself from its tiles, which made its height a
 * consequence of three flex containers agreeing - and they did not: about
 * 180px of empty rail opened up between "1." and "2.". A strip of posters has
 * one obvious height, so it is written down.
 *
 *   poster  128 wide inside a 2px border, at 3/4  ->  171
 *   footer  6 + 6 padding, a 12px name, a 10.5px line -> ~44
 *   borders 2 top + 2 bottom                      ->    4
 *   rail padding 4 + 4                            ->    8
 */
const TILE_WIDTH = 132;
const RAIL_HEIGHT = 227;

/** `2026-09-01` in LOCAL time - what a date input expects. */
function localDateValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

/**
 * The next whole hour, as a sensible default end.
 *
 * Not "now": an override that ends the instant it starts is the one shape of
 * this form that does nothing at all, and it should not be the default.
 */
function nextHourValue(d: Date): string {
  const next = new Date(d.getTime());
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  return `${String(next.getHours()).padStart(2, "0")}:${String(
    next.getMinutes()
  ).padStart(2, "0")}`;
}

export default function SignageChangeChannelDialog({
  open,
  targetName,
  targetScreens,
  playlists,
  onClose,
  onPlayNow,
  onSchedule,
}: SignageChangeChannelDialogProps) {
  const [mode, setMode] = useState<Mode>("now");
  const [step, setStep] = useState<1 | 2>(1);
  const [playlistId, setPlaylistId] = useState("");
  const [endMode, setEndMode] = useState<EndMode>("open");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setMode("now");
    setStep(1);
    setPlaylistId(playlists[0]?.id ?? "");
    // Open-ended is the DEFAULT because it is what the design defaults to, and
    // because the alternative default is a date somebody did not choose.
    setEndMode("open");
    setEndDate(localDateValue(now));
    setEndTime(nextHourValue(now));
    setQuery("");
    setCategory(ALL_CATEGORIES);
    setError(null);
  }, [open, playlists]);

  const selected = playlists.find((p) => p.id === playlistId) ?? null;

  const categories = useMemo(
    () =>
      [
        ...new Set(
          playlists
            .map((p) => p.category?.trim())
            .filter((c): c is string => !!c)
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [playlists]
  );

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return playlists.filter(
      (p) =>
        (category === ALL_CATEGORIES ||
          (p.category?.trim() || "") === category) &&
        (!q || p.name.toLowerCase().includes(q))
    );
  }, [playlists, query, category]);

  /*
   * The chosen end, as an instant - or an explanation of why there isn't one.
   *
   * Computed in one place so the button's disabled state and the value that
   * gets written can never disagree: a form that lets you press a button it
   * will then refuse is worse than one that tells you why it is greyed out.
   */
  const chosenUntil: { at: string | null } | { problem: string } =
    endMode === "open"
      ? { at: null }
      : (() => {
          if (!endDate || !endTime)
            return { problem: "Pick a date and a time." };
          const at = new Date(`${endDate}T${endTime}`);
          if (Number.isNaN(at.getTime()))
            return { problem: "That is not a valid time." };
          if (at.getTime() <= Date.now())
            return { problem: "That moment has already passed." };
          return { at: at.toISOString() };
        })();

  const cont = () => {
    if (mode === "scheduled") {
      // Scheduling is a week of day plans, which is an editor rather than a
      // dialog - and it already exists.
      onClose();
      onSchedule();
      return;
    }
    setStep(2);
  };

  const play = async () => {
    setSaving(true);
    setError(null);
    try {
      if ("problem" in chosenUntil) return;
      await onPlayNow(playlistId, chosenUntil.at);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change the channel");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      // 840, from the design. `sm` (600) squeezed the two mode cards until
      // their descriptions wrapped to three lines each, which is what made
      // them read as dense rather than as a choice between two things.
      maxWidth={false}
      // 980 on the "change now" step, from the design: it is two columns, and
      // the right-hand column exists to show a poster at a size you can
      // actually judge. Step 1 keeps the narrower frame it was drawn at.
      PaperProps={{ sx: { maxWidth: step === 1 ? 840 : 980 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" gap={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: "10px",
              bgcolor: tokens.brandTintSoft,
              display: "grid",
              placeItems: "center",
              color: tokens.brand,
            }}
          >
            <SwapHorizIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              Change channel
            </Typography>
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              {step === 1 ? "Choose how the change happens" : targetName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/*
        A FIXED minimum height across both steps.
        The two steps have very different amounts of content, and a dialog that
        jumps a hundred pixels when you press Continue moves the button you are
        about to press next. The design's generous space below the mode cards
        is the same measurement.
      */}
      <DialogContent dividers sx={{ minHeight: 210 }}>
        {step === 1 ? (
          <>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>
              How do you want to change channel?
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <ModeCard
                selected={mode === "now"}
                onSelect={() => setMode("now")}
                icon={<BoltIcon />}
                title="Change now"
                body="Switch to the chosen content straight away and start playing it."
              />
              <ModeCard
                selected={mode === "scheduled"}
                onSelect={() => setMode("scheduled")}
                icon={<EventOutlinedIcon />}
                title="Schedule a change"
                body="Set when a channel plays, and for how long."
              />
            </Stack>
          </>
        ) : playlists.length === 0 ? (
          <Alert severity="info">
            There is nothing to switch to yet. Make a playlist first and it will
            appear here.
          </Alert>
        ) : (
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={3}
            /*
             * FLEX-START, not stretch.
             *
             * The right column is a tall poster; stretching made the left
             * column adopt its height, and the row of tiles inside it stretched
             * again to fill that - which is where ~180px of nothing between
             * "1." and "2." came from. Each column is now as tall as its own
             * content.
             */
            alignItems="flex-start"
          >
            {/* -- 1. the channel ------------------------------------- */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                1. Choose a channel
              </Typography>
              <Stack direction="row" gap={1.5} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search channels…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  inputProps={{ "aria-label": "Search channels" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  inputProps={{ "aria-label": "Category" }}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value={ALL_CATEGORIES}>All channels</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <ChannelCarousel
                playlists={matching}
                selectedId={playlistId}
                onSelect={setPlaylistId}
              />

              {/* -- 2. how long ------------------------------------- */}
              <Typography sx={{ fontWeight: 700, mt: 2.5, mb: 1.5 }}>
                2. How long should it play?
              </Typography>
              <Stack gap={1.5}>
                <EndCard
                  selected={endMode === "open"}
                  onSelect={() => setEndMode("open")}
                  icon={<AllInclusiveIcon />}
                  title="Until further notice"
                  body="The channel plays until you choose to stop it."
                />
                <EndCard
                  selected={endMode === "until"}
                  onSelect={() => setEndMode("until")}
                  icon={<TimerOutlinedIcon />}
                  title="Set an end time"
                  body="Choose when it stops and the schedule takes over again."
                >
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      inputProps={{ "aria-label": "End date" }}
                    />
                    <TextField
                      type="time"
                      size="small"
                      fullWidth
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      inputProps={{ "aria-label": "End time" }}
                    />
                  </Stack>
                  {"problem" in chosenUntil && (
                    <Typography
                      sx={{ fontSize: 12, color: tokens.error, mt: 1 }}
                    >
                      {chosenUntil.problem}
                    </Typography>
                  )}
                </EndCard>
              </Stack>
            </Box>

            {/* -- the chosen channel, and what it costs --------------- */}
            <Box
              sx={{
                width: { xs: "100%", md: 260 },
                flexShrink: 0,
                borderLeft: { md: `1px solid ${tokens.divider}` },
                pl: { md: 3 },
                // The rule is decoration and must not be what makes the row
                // tall - it stops where the column's own content stops.
                alignSelf: { md: "stretch" },
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                Selected channel
              </Typography>
              <SelectedChannel
                channel={selected}
                targetName={targetName}
                targetScreens={targetScreens}
              />
            </Box>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={step === 1 ? onClose : () => setStep(1)}>
          {step === 1 ? "Cancel" : "Back"}
        </Button>
        <Box sx={{ flex: 1 }} />
        {step === 1 ? (
          <Button variant="contained" onClick={cont}>
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => void play()}
            // Greyed out for a REASON the form is already showing. A button
            // you can press that then refuses is worse than one that is
            // visibly unavailable next to the sentence explaining why.
            disabled={saving || !playlistId || "problem" in chosenUntil}
          >
            {saving ? "Changing…" : "Change now"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ModeCard({
  selected,
  onSelect,
  icon,
  title,
  body,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-label={title}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        flex: 1,
        p: 2,
        borderRadius: "10px",
        cursor: "pointer",
        border: `1px solid ${selected ? tokens.brand : tokens.divider}`,
        bgcolor: selected ? tokens.brandTintSoft : "transparent",
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: tokens.brandTintSoft,
          color: tokens.brand,
          display: "grid",
          placeItems: "center",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          {body}
        </Typography>
      </Box>
      <Radio checked={selected} size="small" tabIndex={-1} />
    </Box>
  );
}

function ChannelChoice({
  playlist,
  selected,
  onSelect,
}: {
  playlist: SignagePlaylist;
  selected: boolean;
  onSelect: () => void;
}) {
  const poster = channelPosterUrl(playlist);
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      // `aria-pressed`, not a label saying "selected": this is a toggle in a
      // group and a screen reader announces the state itself.
      aria-pressed={selected}
      sx={{
        p: 0,
        display: "block",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        bgcolor: tokens.surface,
        border: `2px solid ${selected ? tokens.brand : tokens.divider}`,
        borderRadius: "10px",
        overflow: "hidden",
        transition: "border-color 120ms",
        "&:hover": { borderColor: tokens.brand },
        "&:focus-visible": {
          outline: `2px solid ${tokens.brand}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "3 / 4",
          bgcolor: tokens.rowAlt,
          backgroundImage: poster ? `url(${poster})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        {!poster && (
          <Typography sx={{ fontSize: 11, color: tokens.inkFaint }}>
            No poster
          </Typography>
        )}
        {selected && (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              fontSize: 22,
              color: tokens.brand,
              // The tick sits on artwork we do not control, so it carries its
              // own ground rather than hoping the poster is dark there.
              bgcolor: "#fff",
              borderRadius: "50%",
            }}
          />
        )}
      </Box>
      <Box sx={{ px: 1, py: 0.75, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink }}
        >
          {playlist.name}
        </Typography>
        <Typography noWrap sx={{ fontSize: 10.5, color: tokens.inkMuted }}>
          {playlist.items.length}{" "}
          {playlist.items.length === 1 ? "item" : "items"}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * The channels, as a row you scroll rather than a grid you scan.
 *
 * A grid was right on the Channels PAGE, where browsing the library is the
 * whole task. Here the library is one of four things on screen and the task is
 * "pick one and get on with it", so it gets one row and gives its height back
 * to the end-time question and the preview - which is what the design does.
 *
 * The arrows scroll by a viewport rather than by a card: paging is what the
 * control looks like it does, and nudging one card at a time makes 54 channels
 * feel like a very long row.
 */
function ChannelCarousel({
  playlists,
  selectedId,
  onSelect,
}: {
  playlists: SignagePlaylist[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const rail = useRef<HTMLDivElement>(null);
  const page = (dir: 1 | -1) => {
    const el = rail.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  if (playlists.length === 0) {
    return (
      <Alert severity="info">
        No channel matches that search. Clear it, or pick another category.
      </Alert>
    );
  }

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <IconButton
        size="small"
        onClick={() => page(-1)}
        aria-label="Previous channels"
        sx={{ border: `1px solid ${tokens.divider}`, flexShrink: 0 }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Box
        ref={rail}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          // Fixed, so the strip cannot inherit a height from anything around
          // it. See RAIL_HEIGHT.
          height: RAIL_HEIGHT,
          gap: 1.5,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          // The scrollbar is noise on a row this short, and the arrows already
          // say it scrolls.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          py: 0.5,
        }}
      >
        {playlists.map((p) => (
          <Box
            key={p.id}
            sx={{ flex: `0 0 ${TILE_WIDTH}px`, scrollSnapAlign: "start" }}
          >
            <ChannelChoice
              playlist={p}
              selected={p.id === selectedId}
              onSelect={() => onSelect(p.id)}
            />
          </Box>
        ))}
      </Box>
      <IconButton
        size="small"
        onClick={() => page(1)}
        aria-label="More channels"
        sx={{ border: `1px solid ${tokens.divider}`, flexShrink: 0 }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

/** One of the two answers to "how long", with its own settings folded inside. */
function EndCard({
  selected,
  onSelect,
  icon,
  title,
  body,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <Box
      onClick={onSelect}
      sx={{
        border: `1px solid ${selected ? tokens.brand : tokens.divider}`,
        bgcolor: selected ? tokens.brandTintSoft : tokens.surface,
        borderRadius: "10px",
        p: 2,
        cursor: "pointer",
        transition: "border-color 120ms, background-color 120ms",
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={1.5}>
        <Box
          sx={{ color: selected ? tokens.brand : tokens.inkMuted, mt: 0.25 }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: tokens.ink }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.inkMuted }}>
            {body}
          </Typography>
        </Box>
        <Radio checked={selected} size="small" tabIndex={-1} />
      </Stack>
      {/* The settings belong INSIDE the option they configure - a date picker
          floating below two radios belongs to neither of them. Only rendered
          when chosen, so the unchosen option stays one line. */}
      {selected && children ? <Box sx={{ mt: 2 }}>{children}</Box> : null}
    </Box>
  );
}

/**
 * What you picked, at a size you can judge - and what it will cost.
 *
 * The poster is the point: a venue channel is chosen by how it LOOKS, and the
 * carousel tiles are too small to decide from. The consequence line is there
 * because "change now" is a destructive-feeling action on a wall somebody is
 * looking at, and the dialog should say so before the button does it.
 */
function SelectedChannel({
  channel,
  targetName,
  targetScreens,
}: {
  channel: SignagePlaylist | null;
  targetName: string;
  targetScreens: number;
}) {
  if (!channel) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
        Pick a channel to see it here.
      </Typography>
    );
  }
  const poster = channelPosterUrl(channel);
  return (
    <Stack gap={1.5}>
      <Box
        sx={{
          aspectRatio: "3 / 4",
          borderRadius: "10px",
          bgcolor: tokens.rowAlt,
          backgroundImage: poster ? `url(${poster})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        {!poster && (
          <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
            No poster
          </Typography>
        )}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: tokens.ink }}>
          {channel.name}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: tokens.inkMuted }}>
          {channel.category?.trim() || "Uncategorised"} · {channel.items.length}{" "}
          {channel.items.length === 1 ? "item" : "items"}
        </Typography>
      </Box>
      <Stack
        direction="row"
        gap={1}
        sx={{
          bgcolor: tokens.brandTintSoft,
          borderRadius: "8px",
          p: 1.25,
          color: tokens.inkMuted,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: tokens.brand }} />
        <Typography sx={{ fontSize: 12 }}>
          This replaces what is playing from now.
        </Typography>
      </Stack>
      {/* The real scope, not a number from the mockup: this dialog is opened
          from one screen's card and changes that screen. */}
      <Stack direction="row" alignItems="center" gap={1}>
        <TvOutlinedIcon sx={{ fontSize: 18, color: tokens.inkMuted }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink }}
          >
            {targetScreens} {targetScreens === 1 ? "screen" : "screens"}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: tokens.inkMuted }}>
            {targetName}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}
